import { useContext, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Card, Row, Logo } from 'components/lib';
import { AuthContext } from '../../../app/authContext';
import { authClient, asAuthClientError } from '../../../lib/auth-client';
import { UMButton, UMLoader } from '@urbanmetrix/um-kit';
import { clearPendingInvitationId, setPendingInvitationId } from '../../../lib/pending-invitation';
import { LogoutIcon } from 'components/icons';
import { getApiErrorMessage } from 'api/apiClient';

interface RedirectToAuthOptions {
  shouldRegister: boolean;
  signOutFirst?: boolean;
}

function waitForLoaderPaint() {
  return new Promise<void>((resolve) => {
    requestAnimationFrame(() => requestAnimationFrame(() => resolve()));
  });
}

export function AcceptInvitation() {
  const { isAuthenticated } = useContext(AuthContext) || {};
  const [searchParams] = useSearchParams();
  const invitationId = searchParams.get('id');
  const mustRegister = searchParams.get('mustRegister') === 'true';
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [needsLogin, setNeedsLogin] = useState(false);
  const [isInvitationInvalid, setIsInvitationInvalid] = useState(false);

  const isDisabled = useMemo(
    () => !invitationId || isSubmitting || isInvitationInvalid,
    [invitationId, isSubmitting, isInvitationInvalid]
  );

  useEffect(() => {
    if (invitationId) {
      setPendingInvitationId(invitationId);
    }
  }, [invitationId]);

  async function redirectToAuth({ shouldRegister, signOutFirst = false }: RedirectToAuthOptions) {
    await waitForLoaderPaint();

    if (signOutFirst) {
      try {
        await authClient.signOut();
      } catch (signOutError) {}
    }

    window.location.href = shouldRegister ? '/login?view=register' : '/login';
  }

  async function handleAcceptInvitation() {
    if (!invitationId) {
      setError('Missing invitation id.');
      return;
    }

    if (!isAuthenticated) {
      setNeedsLogin(true);
      await redirectToAuth({ shouldRegister: mustRegister });
      return;
    }

    setIsSubmitting(true);
    setError('');
    setNeedsLogin(false);

    try {
      await authClient.organization.acceptInvitation({
        invitationId,
        fetchOptions: { throw: true },
      });

      clearPendingInvitationId();
      window.location.href = '/dashboard';
    } catch (acceptError) {
      const err = asAuthClientError(acceptError);
      const errorCode = err?.code || err?.error?.code || err?.response?.data?.code;
      const status = err?.status || err?.response?.status;

      if (errorCode === 'INVITATION_NOT_FOUND') {
        clearPendingInvitationId();
        setError('The invitation has expired or is not valid. Please ask the admin to invite you again.');
        setIsInvitationInvalid(true);
        setIsSubmitting(false);
        return;
      }

      if (errorCode === 'YOU_ARE_NOT_THE_RECIPIENT_OF_THE_INVITATION') {
        setError('Log in with the invited email to accept this invitation.');
        setNeedsLogin(true);
        setIsSubmitting(false);
        await redirectToAuth({
          shouldRegister: mustRegister,
          signOutFirst: true,
        });
        return;
      }

      if (errorCode === 'UNAUTHORIZED' || status === 401) {
        setNeedsLogin(true);
        setIsSubmitting(false);
        await redirectToAuth({
          shouldRegister: mustRegister,
          signOutFirst: isAuthenticated,
        });
        return;
      }

      setError(getApiErrorMessage(acceptError, 'Unable to accept invitation.'));
      setIsSubmitting(false);
    }
  }

  async function handleLogout() {
    try {
      await authClient.signOut();
    } finally {
      window.location.href = '/login';
    }
  }

  if (needsLogin) {
    return (
      <div className="auth-loader-screen">
        <UMLoader size={48} />
      </div>
    );
  }

  return (
    <Row title="Accept invitation" customClass="auth-form-row">
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          marginBottom: '15px',
        }}
      >
        <Logo />
      </div>

      <Card restrictWidth center extraClass="form-container" isStat={true}>
        {isInvitationInvalid && (
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '16px' }}>
            <button className="signup-plan-page-logout" type="button" onClick={handleLogout}>
              <LogoutIcon style={{ width: 18, height: 18 }} />
              <span>Logout</span>
            </button>
          </div>
        )}

        <p style={{ marginBottom: '16px' }}>Click below to accept the organization invitation.</p>

        {error && (
          <p className="invitation-error" style={{ marginBottom: '16px' }}>
            {error}
          </p>
        )}

        {!needsLogin && (
          <UMButton
            text={isSubmitting ? 'Accepting...' : 'Accept invitation'}
            onClick={handleAcceptInvitation}
            disabled={isDisabled}
            fullWidth
          />
        )}
      </Card>
    </Row>
  );
}
